import React, {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { Stack, SxProps } from '@mui/material';
import { useElementSize } from 'common/hooks/useElementSize';
import { mergeSxProps } from 'common/theme';
import { assert } from 'common/utils';

const VirtualScrollContext = createContext<{
  scrollContainerHeight: number;
  scrollTop: number;
  scrollContainerEl: HTMLElement | null;
}>({
  scrollContainerHeight: 0,
  scrollTop: 0,
  scrollContainerEl: null,
});

export const useVirtualScrollContext = () => {
  const ctx = useContext(VirtualScrollContext);
  assert(ctx, 'useVirtualScrollContext must be used inside VirtualScroll');
  return ctx;
};

type Props = {
  sx?: SxProps;
  activeScrollSx?: SxProps;
  onScrollTopChange?(value: number): void;
  // Pass the resolved element (e.g. via a useState callback ref in the parent), NOT a RefObject.
  // React's commit phase attaches refs bottom-up, so a child's useLayoutEffect would otherwise
  // run before the parent's ref is populated and miss the element on first mount.
  scrollContainerEl?: HTMLElement | null;
};

export const VirtualScroll = ({
  children,
  sx,
  activeScrollSx,
  onScrollTopChange,
  scrollContainerEl,
}: PropsWithChildren<Props>) => {
  const useExternalScroll = scrollContainerEl !== undefined;

  const [internalContainerRef, { height: internalContainerHeight }] = useElementSize();
  const [internalScrollTop, setInternalScrollTop] = useState(0);

  const [externalScrollTop, setExternalScrollTop] = useState(0);
  const [externalContainerHeight, setExternalContainerHeight] = useState(0);

  // Stash the callback in a ref so the scroll/resize listeners don't need to be
  // re-subscribed every time the consumer passes a new function identity.
  const onScrollTopChangeRef = useRef(onScrollTopChange);
  useEffect(() => {
    onScrollTopChangeRef.current = onScrollTopChange;
  }, [onScrollTopChange]);

  useLayoutEffect(() => {
    if (!scrollContainerEl) return;
    const el = scrollContainerEl;

    const updateScroll = () => {
      setExternalScrollTop(el.scrollTop);
      onScrollTopChangeRef.current?.(el.scrollTop);
    };
    const updateSize = () => setExternalContainerHeight(el.clientHeight);
    updateScroll();
    updateSize();

    el.addEventListener('scroll', updateScroll, { passive: true });
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener('scroll', updateScroll);
      resizeObserver.disconnect();
    };
  }, [scrollContainerEl]);

  const handleInternalScroll: React.UIEventHandler<HTMLDivElement> = useCallback((e) => {
    setInternalScrollTop(e.currentTarget.scrollTop);
    onScrollTopChangeRef.current?.(e.currentTarget.scrollTop);
  }, []);

  const contextValue = useMemo(
    () =>
      useExternalScroll
        ? {
            scrollTop: externalScrollTop,
            scrollContainerHeight: externalContainerHeight,
            scrollContainerEl: scrollContainerEl ?? null,
          }
        : {
            scrollTop: internalScrollTop,
            scrollContainerHeight: internalContainerHeight,
            scrollContainerEl: null,
          },
    [externalScrollTop, externalContainerHeight, scrollContainerEl, internalScrollTop, internalContainerHeight]
  );

  if (useExternalScroll) {
    return <VirtualScrollContext.Provider value={contextValue}>{children}</VirtualScrollContext.Provider>;
  }

  return (
    <VirtualScrollContext.Provider value={contextValue}>
      <Stack
        ref={internalContainerRef}
        sx={mergeSxProps({ overflowY: 'auto' }, sx, internalScrollTop ? activeScrollSx : undefined)}
        onScroll={handleInternalScroll}
      >
        {children}
      </Stack>
    </VirtualScrollContext.Provider>
  );
};
